import { getSimpleIdentifierFromString, Identifiable, OrganizationalUnitDTO } from './../../api/plyca-dtos';
import {
  BaseFormComponent,
  BaseFormHeaderInfo
} from '../../tasks/baseFormComponent/baseForm.component';
import {
  CommunicationDTO,
  CommunicationAgentDTO,
  CommunicationStatusDTO,
  CommunicationStatusType,
  CommunicationDirectionType,
  CommunicationActivityDTO,
  CommunicationChannelDTO,
  ProjectEntityDTO,
  ValidationResultDTO,
  ResultTypeDTO,
  DocumentCategories
} from '../../api/plyca-dtos-v2';
import {
  Component,
  OnInit,
  Optional,
  ElementRef,
  ViewChild,
  TemplateRef,
  OnDestroy
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { tap, catchError, map, finalize, switchMap } from 'rxjs/operators';
import {
  Observable,
  throwError,
  forkJoin,
  of,
  ObservableInput,
  concat
} from 'rxjs';
import { TemplateDef, FormConfig } from '../../components/forms/form.objects';
import {
  DivisionDocumentClassType,
  TenderingPhaseDTO
} from '../../api/plyca-dtos';
import { InterfaceSettings } from '../../api/contract-notice-dto';
import { generateRandomIdentifier } from '../../ui.service';
import {
  HelpService,
  HelpType
} from '../../components/common/help/help.service';
import {
  DialogService,
  DynamicDialogRef,
  DynamicDialogConfig
} from 'primeng-lts/dynamicdialog';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';

export class CommunicationEditionModel {
  dto: CommunicationDTO;
  ui: {
    orgReceiver?: any;
    receiverType?: string;
    edition?: boolean;
    title?: string;
    from?: string;
    to?: string;
    labels?: any;
    isTask?: boolean;
  };
}

@Component({
  selector: 'communication-form',
  templateUrl: './communication.edition.template.html',
  providers: [DialogService]
})
export class CommunicationEditionComponent
  extends BaseFormComponent<CommunicationDTO, CommunicationEditionModel>
  implements OnInit, OnDestroy {
  private identifier: string;
  private projectId: string;
  private activityId: string;
  private channelId: string;
  private communicationToReply: CommunicationDTO;
  tagsParent: string[];
  private editDialogOpened: boolean;

  public isModalWindow: boolean;
  /**
   * Propiedad que indica si se accede a la pantalla
   */
  public edition = true;
  @ViewChild('docsTemplate')
  docsTemplate: TemplateRef<any>;
  @ViewChild('fromTemplate')
  fromTemplate: TemplateRef<any>;
  @ViewChild('toTemplate')
  toTemplate: TemplateRef<any>;

  public DOCUMENTCLASS_AWARD = DivisionDocumentClassType._02001.identifier;

  constructor(
    elem: ElementRef,
    public helpService: HelpService,
    private plycaErrorHandler: PlycaErrorHandler,
    @Optional() protected dialogRef: DynamicDialogRef,
    @Optional() protected dialogConfig: DynamicDialogConfig,
    @Optional() protected activatedRoute: ActivatedRoute
  ) {
    super(elem);

    this.isModalWindow = this.dialogConfig && this.dialogConfig.data;

    this.subscribe(
      this.helpService
        .getStoreTagsObservable()
        .pipe(switchMap((helpTags) => (this.tagsParent = helpTags)))
    );

    if (!this.task) {
      if (!this.isModalWindow) {
        // acceso a traves de route
        this.subscribe(
          this.activatedRoute.params.pipe(
            tap((params) => {
              // let observable: Observable<any>;
              this.identifier = params['id'];
              this.projectId = params['project'];
              this.channelId = params['channel'];
              this.activityId = params['activity'];
            })
          )
        );
        // obtener otros parametros
        this.subscribe(
          this.activatedRoute.queryParams.pipe(
            tap((params) => {
              this.edition = 'true' === params['edit'];
            })
          )
        );
      } else {
        // se abre como dialogo
        let communicationToEdit: CommunicationDTO;
        // responder a una comunicacion que se pasa por parametro
        if (this.dialogConfig.data.communication) {
          communicationToEdit = this.dialogConfig.data
            .communication as CommunicationDTO;
          if (this.dialogConfig.data.reply) {
            this.communicationToReply = communicationToEdit;
          } else {
            this.identifier = '' + communicationToEdit.identifier;
          }
        }
        if (
          communicationToEdit &&
          communicationToEdit.project &&
          communicationToEdit.project.identifier
        ) {
          this.projectId = communicationToEdit.project.identifier;
        }
        if (
          communicationToEdit &&
          communicationToEdit.channel &&
          communicationToEdit.channel.identifier
        ) {
          this.channelId = communicationToEdit.channel.identifier;
        }
        if (
          communicationToEdit &&
          communicationToEdit.activity &&
          communicationToEdit.activity.identifier
        ) {
          this.activityId = communicationToEdit.activity.identifier;
        }

        this.edition = this.dialogConfig.data.edition;
        this.windowHash = generateRandomIdentifier();
      }
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();

    this.subscribe(
      this.helpService.showContextHelpObservable(this.tagsParent).pipe(
        finalize(() => {
          this.dialogConfig = null;
          this.dialogService = null;
        })
      )
    );
  }

  getFormSettingURL(): string {
    return '/dossier/communication/communication.json';
  }

  getServiceURL(): string {
    return this.configuration.COMMUNICATIONS_SERVICE_URL;
  }

  getObjectIdentifier(): string {
    let identifier: string;
    if (this.task) {
      identifier = undefined;
    } else if (this.identifier) {
      identifier = this.identifier;
    } else {
      // nueva comunicacion
      identifier = 'new';
    }
    return identifier;
  }

  getObjectParams(): { [param: string]: string | string[] } {
    let params: { [param: string]: string | string[] };
    if (this.identifier) {
      params = {};
    } else {
      params = {
        channelId: this.channelId,
        activityId: this.activityId,
      };
    }
    params.projectIdentifier = this.projectId; // TODO: revisar que este valor va nulo
    params.expand = 'expansionSet:web.detail,repliedCommunication';
    return params;
  }

  getProjectParams(): { [param: string]: string | string[] } {
    return {
      expand: 'expansionSet:minimal',
    };
  }

  dtoToModel(dto: CommunicationDTO): Observable<CommunicationEditionModel> {
    let model = new CommunicationEditionModel();
    // inicializacion de los campos del modelo utilizados por el json
    if (this.task) {
      model.dto = this.task.work.communication;
    } else {
      model.dto = dto;
    }
    model.ui = {};
    model.ui.isTask = this.task !== undefined;
    model.ui.edition = this.task ? true : this.edition;
    model.ui.receiverType = 'LIC'; // canal registro, valor por defecto
    let modelObs: Observable<CommunicationEditionModel>;
    if (this.identifier) {
      if (model?.dto?.receivers && model?.dto?.receivers[0]?.agentClass == DocumentCategories.Organismo) {
        model.ui.receiverType = 'ORG';
        model.ui.orgReceiver = model?.dto?.receivers[0]?.organizationalUnit;
        model.ui.orgReceiver.identifier =
          model?.dto?.receivers[0]?.organizationalUnit?.identifier;
      }
      modelObs = of(model);
    } else {
      // pedimos los datos basicos de expediente actividad y canal para mostrar informacion en pantalla
      modelObs = forkJoin(
        this.plycaService.get<ProjectEntityDTO>(
          this.configuration.PROJECTENTITY_SERVICE_URL,
          this.projectId,
          this.getProjectParams()
        ),
        this.plycaService.get<CommunicationChannelDTO>(
          this.configuration.COMMUNICATIONCHANNEL_SERVICE_URL,
          this.channelId
        ),
        this.plycaService.get<CommunicationActivityDTO>(
          this.configuration.COMMUNICATIONACTIVITY_URL,
          this.activityId
        )
      ).pipe(
        map((result: any) => {
          let index = 0;
          model.dto.project = result[index++];
          model.dto.channel = result[index++];
          model.dto.activity = result[index++];
          if (!this.identifier && this.communicationToReply) {
            model.dto.repliedCommunication = this.communicationToReply;
            if (this.communicationToReply.sender) {
              model.dto.receivers = new Array<CommunicationAgentDTO>();
              model.dto.receivers.push(this.communicationToReply.sender);
            }
            if (
              this.communicationToReply.receivers &&
              this.communicationToReply.receivers.length > 0
            ) {
              model.dto.sender = this.communicationToReply.receivers[0];
            }
          }
          return model;
        })
      );
    }
    return modelObs;
  }

  modelToDTO(model: CommunicationEditionModel): CommunicationDTO {
    let dto = model.dto;
    let ui = model.ui;
    // eliminar eventos, errores y acciones que no se guardan a traves del servicio
    delete dto.events;
    delete dto.errors;
    delete dto.actions;
    // eliminar referencias circulares para evitar problemas en el envÃ­o
    if (dto.documents) {
      dto.documents.forEach((document) => delete document.parent);
    }
    if (dto.sender) {
      delete dto.sender.parent;
    }
    if (dto.receivers) {
      dto.receivers.forEach((receiver) => delete receiver.parent);
    }
    if (dto.params) {
      dto.params.forEach((param) => delete param.parent);
    }
    if (!dto.identifier) {
      // las comunicaciones que se crean son de salida
      dto.direction = CommunicationDirectionType.OUT;
    }
    return dto;
  }
  getProjectId(): string {
    return this.model.dto.project.identifier;
  }

  processTask(reactivate = false) {
    // eliminar eventos, errores y acciones que no se guardan a traves del servicio
    const communication = this.model.dto;
    this.task.work.communication = communication;
    delete communication.events;
    delete communication.errors;
    delete communication.actions;
    super.processTask(reactivate);
  }

  getHeaderInfo(): BaseFormHeaderInfo {
    let headerInfo: BaseFormHeaderInfo;
    // si se abre como tarea el componente baseTask.component pone la informacion
    if (!this.task) {
      headerInfo = new BaseFormHeaderInfo();
      let activity = this.model.dto.activity;
      let channel = this.model.dto.channel;
      headerInfo.title =
        (activity ? activity.description : '') +
        ' - ' +
        (channel ? channel.description : '');
      headerInfo.titleIcon = 'fas fa-envelope-open-text';
      headerInfo.project = this.model.dto.project;
    }
    return headerInfo;
  }

  protected createFormConfig(): Observable<FormConfig> {
    let dto = this.model.dto;
    let baseFormURL = this.getFormSettingURL();
    let channelFormObs: Observable<InterfaceSettings>;
    let activityFormObs: Observable<InterfaceSettings>;
    if (dto.channel && dto.channel.identifier) {
      let channeFormURL =
        'dossier/communication/channel/' + dto.channel.identifier + '.json';
      channelFormObs = this.plycaService
        .getInterfaceSettings(channeFormURL, true)
        .pipe(catchError((e: any) => this.ignoreError(e, channeFormURL)));
    } else {
      channelFormObs = of(undefined);
    }
    if (dto.activity && dto.activity.identifier) {
      let activityFormURL =
        'dossier/communication/channel/activity/' +
        dto.activity.identifier +
        '.json';
      activityFormObs = this.plycaService
        .getInterfaceSettings(activityFormURL, true)
        .pipe(catchError((e: any) => this.ignoreError(e, activityFormURL)));
    } else {
      activityFormObs = of(undefined);
    }

    return forkJoin(
      this.plycaService.getInterfaceSettings(baseFormURL),
      channelFormObs,
      activityFormObs
    ).pipe(
      map((result: InterfaceSettings[]) => {
        let mergedInterfacceSettings = result[0];
        let interfaceToMerge = result[1];
        if (interfaceToMerge) {
          mergedInterfacceSettings = this.formService.mergeInterfaceSettings(
            mergedInterfacceSettings,
            interfaceToMerge
          );
        }
        interfaceToMerge = result[2];
        if (interfaceToMerge) {
          mergedInterfacceSettings = this.formService.mergeInterfaceSettings(
            mergedInterfacceSettings,
            interfaceToMerge
          );
        }
        let formConfig = this.newFormConfig();
        formConfig.interfaceSetting = mergedInterfacceSettings;
        formConfig.showValidations = true;
        return formConfig;
      })
    );
  }

  private ignoreError(
    e: any,
    channeFormURL: string
  ): ObservableInput<InterfaceSettings> {
    console.error(
      'Error al obtener definicion del formulario ' + channeFormURL
    );
    return of(undefined);
  }

  initHeaderActions() {
    let activityId: string;
    let channelId: string;

    if (this.task && this.task.work && this.task.work.communication) {
      activityId = this.task.work.communication.activity.identifier;
      channelId = this.task.work.communication.channel.identifier;

      this.tagsParent.push(HelpType.DOSSIER_COMUNICATIONS_INFO);
      this.tagsParent.push('CANAL_' + channelId);
      this.tagsParent.push('ACT_' + activityId);
      this.subscribe(
        this.helpService.showContextHelpObservable(this.tagsParent)
      );
    } else if (
      this.model &&
      this.model.dto.channel &&
      this.model.dto.activity
    ) {
      this.subscribe(
        this.helpService.showContextHelpObservable([
          HelpType.DOSSIER_COMUNICATIONS_INFO,
          'CANAL_' + this.model.dto.channel.identifier,
          'ACT_' + this.model.dto.activity.identifier,
        ])
      );
    }

    // si se abre como tarea el componente baseTask.component pone las acciones
    this.headerActionsService.clearWindowActions(this.windowHash);
    if (!this.task) {
      if (this.edition) {
        this.subscribe(
          this.headerActionsService.addWindowAction(
            'General.save',
            'fas fa-save',
            'btn-outline-primary',
            true,
            this.windowHash
          ),
          () => {
            this.model.dto.status = new CommunicationStatusDTO(
              CommunicationStatusType.EDITING
            );
            this.subscribe(this.save(), () => this.salir());
          }
        );
        this.subscribe(
          this.headerActionsService.addWindowAction(
            'Communications.send',
            'far fa-paper-plane',
            'btn-primary',
            true,
            this.windowHash
          ),
          () => {
            this.waitService.startWaiting('Enviando...');
            this.model.dto.status = new CommunicationStatusDTO(
              CommunicationStatusType.SENDING
            );
            this.subscribe(
              super.saveIfValidationLevel(false, ResultTypeDTO.WARNING).pipe(
                tap((result: any) => {
                  if (
                    !result ||
                    !this.formService.isValidationResponse(result)
                  ) {
                    this.salir();
                  } else {
                    this.waitService.cancelWaiting();
                  }
                }),
                catchError((err: any) => {
                  this.waitService.cancelWaiting();
                  this.plycaErrorHandler.showServerError(err);
                  return throwError(err);
                })
              )
            );
          }
        );
      } else if (
        this.model.dto.direction === CommunicationDirectionType.IN &&
        this.model.dto.status &&
        this.model.dto.status.type === CommunicationStatusType.ERROR
      ) {
        // si es una comunicacion de entrada con errores permitir reejecutar
        this.subscribe(
          this.headerActionsService.addWindowAction(
            'Communications.retry',
            'fas fa-redo',
            'btn-primary',
            true,
            this.windowHash
          ),
          () => this.processCommunication()
        );
      }
      if (!this.isModalWindow) {
        this.headerActionsService.addWindowDivider(this.windowHash);
        this.addBackAction();
      }
    } else {
      this.headerActionsService.clearWindowActions(this.windowHash);
      this.subscribe(
        this.headerActionsService.addWindowAction(
          'General.accept',
          'fas fa-lg fa-check',
          'btn-primary',
          true,
          this.windowHash
        ),
        (success) => {
          this.waitService.startWaiting();
          this.subscribe(
            this.validate(false),
            (validation: ValidationResultDTO) => {
              if (validation.result !== ResultTypeDTO.FATAL) {
                this.processTask();
              } else {
                this.waitService.cancelWaiting();
              }
            }
          );
        }
      );
      this.headerActionsService.addWindowDivider(this.windowHash);
      this.subscribe(
        this.headerActionsService.addWindowAction(
          'General.cancel',
          'fas fa-times',
          'btn-outline-secondary',
          true,
          this.windowHash
        ),
        (success) => this.salir()
      );
    }
    // emisor
    let sender = this.model.dto.sender;
    this.model.ui.from = sender
      ? CommunicationAgentDTO.getAgentDescription(sender)
      : '';
    // destinatario
    let receivers = this.model.dto.receivers;
    if (
      !this.edition ||
      this.model.dto.direction === CommunicationDirectionType.IN
    ) {
      // en una comunicacion de entrada el destinatario no es editable
      this.model.ui.to = receivers
        ? CommunicationAgentDTO.getAgentListDescription(receivers)
        : '';
    }
  }
  private processCommunication() {
    this.model.dto.status = new CommunicationStatusDTO(
      CommunicationStatusType.SENDING
    );
    this.waitService.startWaiting();
    this.subscribe(
      this.plycaService
        .patch<CommunicationDTO>(this.getServiceURL(), this.model.dto)
        .pipe(
          finalize(() => {
            this.waitService.cancelWaiting();
          })
        ),
      () => this.salir(),
      (error) => this.manageActionError(error, true)
    );
  }

  private newFormConfig(): FormConfig {
    let formConfig: FormConfig;
    formConfig = new FormConfig(
      this.getFormSettingURL(),
      undefined,
      this.model
    );
    let templates = new Array<TemplateDef>();
    // template emisor
    let fromTemplate = new TemplateDef();
    fromTemplate.identifier = 'sender';
    fromTemplate.template = this.fromTemplate;
    templates.push(fromTemplate);
    // template receptores
    let toTemplate = new TemplateDef();
    toTemplate.identifier = 'receivers';
    toTemplate.template = this.toTemplate;
    templates.push(toTemplate);
    // template documentos
    let docsTemplate = new TemplateDef();
    docsTemplate.identifier = 'documents';
    docsTemplate.template = this.docsTemplate;
    templates.push(docsTemplate);

    formConfig.templates = templates;
    // funciones
    formConfig.functions = [];
    formConfig.searchTags = [HelpType.EXPENSE_DOCUMENT];
    formConfig.functions.push(
      {
        identifier: 'tenderingPhaseDescription',
        fn: this.fnTenderingPhaseDescription.bind(this)
      },
      {

        identifier: 'addOrgReceiver',
        fn: this.addOrgReceiver
      }

    );
    return formConfig;
  }

  canDeactivate(): boolean | Observable<boolean> | Promise<boolean> {
    if (!this.editDialogOpened) {
      // hay un dialogo de edicion abierto
      // permitir salir si no hay tarea, o lo permite la tarea
      return !this.task || super.canDeactivate();
    }
    return false;
  }

  public setEditDialogOpened(editing: boolean) {
    this.editDialogOpened = editing;
  }

  // Funciones del formulario
  public fnTenderingPhaseDescription(phase: TenderingPhaseDTO): string {
    let phaseDesc =
      this.translateService.instant('TenderingPhase.envelopes') +
      ': ' +
      TenderingPhaseDTO.getEnvelopesDescription(phase);
    return '"' + phaseDesc + '"';
  }


  public addOrgReceiver(model: any, result: any) {
    model.ui.orgReceiver = result;
    let agent = new CommunicationAgentDTO();
    agent.agentClass = DocumentCategories.Organismo;
    agent.organizationalUnit = new OrganizationalUnitDTO();
    agent.organizationalUnit.identifier = getSimpleIdentifierFromString(result.identifier);
    agent.organizationalUnit.name = result.description;
    model.dto.receivers = [agent];
  }


  salir() {
    if (this.isModalWindow) {
      this.waitService.cancelWaiting();
      this.dialogRef.close(this.model.dto);
    } else {
      this.uiService.routeBack();
    }
  }
}
